import Breadcrumb from "components/Common/Breadcrumb"
import Loader from "components/Loader/loader"
import React, { useState, useMemo, useRef, useEffect } from "react"
import {
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "reactstrap"
import {
  FaFileImport,
  FaTrash,
  FaChevronLeft,
  FaChevronRight,
  FaFilePdf,
  FaFileExport,
  FaEye,
  FaEdit,
} from "react-icons/fa"
import { MdCancel, MdSave } from "react-icons/md"
import {
  fetchData,
  FetchDeleteData,
  FetchPutData,
} from "utils/Common_api_functions"
import Swal from "sweetalert2"
import Configs from "../../config"
import { useNavigate } from "react-router-dom"
import Select from "react-select"
import ExcelJS from "exceljs"
import { customStyles } from "utils/SelectStyles"
import { usePermissionData } from "AppContext"

const toInputDate = raw => {
  if (!raw) return ""
  const str = String(raw).split(" ")[0]
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str
  const parts = str.split("/")
  if (parts.length === 3) {
    let [a, b, c] = parts
    if (c.length === 2) c = "20" + c
    return `${c}-${b.padStart(2, "0")}-${a.padStart(2, "0")}`
  }
  return ""
}

// Add this helper above your component or inside it
const safeParseLineItems = lineItems => {
  try {
    if (!lineItems) return []

    // Already an array — no parsing needed
    if (Array.isArray(lineItems)) return lineItems

    // First parse
    let parsed = JSON.parse(lineItems)

    // If result is still a string — double stringified, parse again
    if (typeof parsed === "string") {
      parsed = JSON.parse(parsed)
    }

    return Array.isArray(parsed) ? parsed : []
  } catch (e) {
    console.warn("lineItems parse error:", e)
    return []
  }
}

const roundToFive = val => Math.round(val / 5) * 5

/* ═══════════════════════════════════════════════════════════════ */
const DocumentIndex = () => {
  document.title = "Inventory | Milano Jewelry"
  const navigate = useNavigate() 

  const { checkAccess } = usePermissionData()

  const [vendors, setVendors] = useState([])
  const [loading, setLoading] = useState(false)
  const [documents, setDocuments] = useState([])
  const [searchString, setSearchString] = useState("")
  const [pageSize, setPageSize] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)

  const [selectedDocument, setSelectedDocument] = useState({})
  const [dropdownMap, setDropdownMap] = useState({})
  const [products, setProducts] = useState([])

  const [docModal, setDocModal] = useState(false)
  const [isEditing, setIsEditing] = useState(false)

  useEffect(() => {
    fetchVendors()
    fetchDocuments()
    fetchCategories()
  }, [])

  const createDropdownMap = data => ({
    department: {
      list: data?.departments || [],
      label: "departmentName",
    },
    category: {
      list: data?.categories || [],
      label: "categoryName",
    },
    class: {
      list: data?.classes || [],
      label: "className",
    },
    programBuild: {
      list: data?.programBuilds || [],
      label: "programBuildName",
    },
    stone: {
      list: data?.stones || [],
      label: "stoneName",
    },
    brand: {
      list: data?.brands || [],
      label: "brandName",
    },
    store: {
      list: data?.stores || [],
      label: "Store",
    },
  })

  async function fetchCategories() {
    const [APIdata, vmCode1Res, vmCode5Res, vmCode6Res, vmCode7Res, vmCode8Res, compTypeRes, compType2Res, compType3Res, compType4Res, compType5Res] =
      await Promise.all([
        fetchData(setLoading, "products/d/c/c/p/s/b"),
        fetchData(() => {}, "vm/code/one"),
        fetchData(() => {}, "vm/code/five"),
        fetchData(() => {}, "vm/code/six"),
        fetchData(() => {}, "vm/code/seven"),
        fetchData(() => {}, "vm/code/eight"),
        fetchData(() => {}, "component/type/one"),
        fetchData(() => {}, "component/type/two"),
        fetchData(() => {}, "component/type/three"),
        fetchData(() => {}, "component/type/four"),
        fetchData(() => {}, "component/type/five"),
      ])

    if (APIdata?.success) {
      const map = createDropdownMap(APIdata)
      map.vmCode = { list: vmCode1Res?.data || [], label: "VMCodeOne" }
      map.vmCode5 = { list: vmCode5Res?.data || [], label: "VMCodeFive" }
      map.vmCode6 = { list: vmCode6Res?.data || [], label: "VMCodeSix" }
      map.vmCode7 = { list: vmCode7Res?.data || [], label: "VMCodeSeven" }
      map.vmCode8 = { list: vmCode8Res?.data || [], label: "VMCodeEight" }
      map.componentType = { list: compTypeRes?.data || [], label: "componentTypeOne" }
      map.componentType2 = { list: compType2Res?.data || [], label: "componentTypeTwo" }
      map.componentType3 = { list: compType3Res?.data || [], label: "componentTypeThree" }
      map.componentType4 = { list: compType4Res?.data || [], label: "componentTypeFour" }
      map.componentType5 = { list: compType5Res?.data || [], label: "componentTypeFive" }
      setDropdownMap(map)
    }
  }
  async function fetchVendors() {
    const APIdata = await fetchData(setLoading, "vendor")
    if (APIdata?.success) setVendors(APIdata?.data)

    const APIdata1 = await fetchData(setLoading, "products")
    if (APIdata?.success) setProducts(APIdata1?.data)
  }

  async function fetchDocuments() {
    const APIdata = await fetchData(setLoading, "invoice")
    if (APIdata?.success) {
      setDocuments(APIdata?.data?.reverse())
    }
  }

  /* ── Table helpers ── */
  const filteredData = useMemo(
    () =>
      documents.filter(doc =>
        [
          doc.invoiceLink,
          doc.invoiceNumber,
          doc?.vendor?.company,
          doc.status,
          doc?.totalAmount,
        ]
          .join(" ")
          .toLowerCase()
          .includes(searchString.toLowerCase())
      ),
    [documents, searchString]
  )

  const totalPages = Math.ceil(filteredData.length / pageSize)
  const paginatedData = filteredData.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  )

  const getPageNumbers = () => {
    let start = Math.max(currentPage - 2, 1)
    let end = Math.min(start + 4, totalPages)
    if (end - start < 4) start = Math.max(end - 4, 1)
    return Array.from({ length: end - start + 1 }, (_, i) => start + i)
  }

  const handleDelete = async id => {
    const APIdata = await FetchDeleteData(setLoading, "invoice", id)
    if (APIdata?.data?.success) {
      Swal.fire({
        title: "Document Deleted Successfully",
        text: APIdata?.data?.message,
        icon: "success",
      })
      fetchDocuments()
    } else {
      Swal.fire({
        title: "Error deleting Document",
        text: APIdata?.response?.data?.message,
        icon: "error",
      })
    }
  }

  /* ── Field updaters ── */
  const updateField = (key, value) =>
    setSelectedDocument(prev => {
      const updated = { ...prev, [key]: value }

      if (key === "invMarkup") {
        const markup = Number(value || 0)
        const items = safeParseLineItems(prev.lineItems) || []

        const updatedItems = items.map(item => {
          const rate = Number(item?.rate || 0)
          const discount = Number(item?.discount || 3)

          const optionalPrice = rate + rate * (markup / 100)
          const sellcost = optionalPrice * discount

          return {
            ...item,
            optionalPrice: roundToFive(optionalPrice),
            sellcost: roundToFive(sellcost),
          }
        })

        updated.lineItems = JSON.stringify(updatedItems)
      }

      if (key === "msrpMarkup") {
        const msrp = Number(value || 0)
        const invMarkupVal = Number(prev?.invMarkup || 0)
        const items = safeParseLineItems(prev.lineItems) || []

        const updatedItems = items.map(item => {
          const rate = Number(item?.rate || 0)
          const optionalPrice = rate + rate * (invMarkupVal / 100)
          const sellcost = optionalPrice * msrp

          return {
            ...item,
            discount: msrp,
            optionalPrice: roundToFive(optionalPrice),
            sellcost: roundToFive(sellcost),
          }
        })

        updated.lineItems = JSON.stringify(updatedItems)
      }

      return updated
    })

  const updateLineItem = (index, field, value) =>
    setSelectedDocument(prev => {
      const items = safeParseLineItems(prev.lineItems) || []

      const updatedItem = {
        ...items[index],
        [field]: ["qty", "rate", "amount"].includes(field)
          ? Number(value)
          : value,
      }

      // Recalculate when rate/discount changes
      if (field === "rate" || field === "discount") {
        const rate =
          field === "rate" ? Number(value) : Number(updatedItem.rate || 0)

        const discount =
          field === "discount"
            ? Number(value)
            : Number(updatedItem.discount || 0)

        const invMarkupVal = Number(prev?.invMarkup || 0)
        const optionalPrice = rate + rate * (invMarkupVal / 100)
        const sellcost = optionalPrice * discount

        updatedItem.optionalPrice = roundToFive(optionalPrice)
        updatedItem.sellcost = roundToFive(sellcost)
      }

      if (field === "sku") {
        const newProduct = products.find(item => item?.invSkuNumber == value)

        if (newProduct) {
          updatedItem.description = newProduct.productName
          updatedItem.department = newProduct.departmentId
          updatedItem.category = newProduct.categoryId
          updatedItem.class = newProduct.classId
          updatedItem.programBuild = newProduct.programBuildId
          updatedItem.stone = newProduct.stoneId
          updatedItem.brand = newProduct.brandId
          updatedItem.componentType = newProduct.componentTypeOneId
          updatedItem.componentCode = newProduct.componentCode
          updatedItem.vmCode = newProduct.vmCode
          updatedItem.vmCode5 = newProduct.vmCodeFiveId
          updatedItem.vmCode6 = newProduct.vmCodeSixId
          updatedItem.vmCode7 = newProduct.vmCodeSevenId
          updatedItem.vmCode8 = newProduct.vmCodeEightId
        }
      }

      items[index] = updatedItem

      return {
        ...prev,
        lineItems: JSON.stringify(items),
      }
    })

  const handleEdit = async () => {
    if (!Number(selectedDocument?.invMarkup)) {
      Swal.fire({ title: "Store Markup cannot be zero", icon: "warning" })
      return
    }
    if (!Number(selectedDocument?.msrpMarkup)) {
      Swal.fire({ title: "MSRP Markup cannot be zero", icon: "warning" })
      return
    }

    try {
      const payload = {
        supplierCountry: selectedDocument?.supplierCountry,
        store: selectedDocument?.store,
        msrpMarkup: selectedDocument?.msrpMarkup,
        promotionCode: selectedDocument?.promotionCode,
        vendorName: selectedDocument?.vendor?.vendorName,
        invoiceNo: selectedDocument?.invoiceNo,
        memoNo: selectedDocument?.memoNo,
        invoiceDate: selectedDocument?.invoiceDate,
        poNumber: selectedDocument?.poNumber,
        dueDate: selectedDocument?.dueDate,
        trackingNo: selectedDocument?.trackingNo,
        totalAmount: selectedDocument?.totalAmount,
        billTo: selectedDocument?.billTo,
        lineItems: selectedDocument?.lineItems,
      }

      const APIdata = await FetchPutData(
        setLoading,
        "invoice",
        selectedDocument?.id,
        payload
      )

      if (APIdata?.data?.success) {
        Swal.fire({
          icon: "success",
          title: "Document Updated Successfully",
          text: APIdata?.data?.message,
        })
        fetchDocuments()
        setDocModal(false)
        setSelectedDocument({})
      } else {
        Swal.fire({
          icon: "error",
          title: "Update Failed",
          text: APIdata?.response?.data?.message || "Something went wrong",
        })
      }
    } catch (error) {
      console.error("handleEdit error:", error)
      Swal.fire({
        icon: "error",
        title: "Unexpected Error",
        text: "Please try again",
      })
    }
  }

  const handleExport = async doc => {
    const lineItems = safeParseLineItems(doc.lineItems)

    const headerRow1 = [
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "CERTIFICATE",
      "",
      "",
      "",
      "RING SIZE AND CHAIN LENGTH",
      "CHAIN TYPE",
      "SET COUNT",
      "METAL TYPE",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "DEPARTMENT",
      "CATEGORY",
      "CLASS",
      "STONE",
      "PROGRAM BUILD",
      "BRAND",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "COMPONENTS- TYPE",
      "COMPONENTS- CODE",
      "",
      "",
      "COMPONENTS- QUANTITY",
    ]

    const headerRow2 = [
      "SKU",
      "Description",
      "STYLE",
      "Detail Desc",
      "SKU Major Code",
      "Vendor",
      "Individual Cost Value",
      "Vendor Model",
      "Individual Selling Value",
      "VM Code 1",
      "VM Code 2",
      "VM Code 3",
      "VM Code 4",
      "VM Code 5",
      "VM Code 6",
      "VM Code 7",
      "VM Code 8",
      "VM Code 9",
      "VM Code 10",
      "VM Code 11",
      "VM Code 12",
      "VM Code 13",
      "VM Code 14",
      "VM Code 15",
      "Inventory Cat 1",
      "Inventory Cat 2",
      "Inventory Cat 3",
      "Inventory Cat 4",
      "Inventory Cat 5",
      "Inventory Cat 6",
      "Store",
      "Showcase",
      "Quantity",
      "Vendor Invoice",
      "Promotion Code",
      "Country of Origin",
      "B2BPrice",
      "RetailPrice",
      "Buyer",
      "Internet Length",
      "Internet Width",
      "Internet Height",
      "Web Enabled",
      "Memo",
      "Wholesale Enabled",
      "Upload Picture",
      "Optional Price 1",
      "Attribute",
      "Component 1 Type",
      "Component 1 Code",
      "Component 1 Item #",
      "Component 1 Desc",
      "Component 1 Qty",
      "Component 1 Cost",
      "Component 1 Element 1",
      "Component 1 Element 2",
      "Component 1 Element 3",
      "Component 1 Element 4",
      "Component 1 Element 5",
      "Component 2 Type",
      "Component 2 Code",
      "Component 2 Item #",
      "Component 2 Desc",
      "Component 2 Qty",
      "Component 2 Cost",
      "Component 2 Element 1",
      "Component 2 Element 2",
      "Component 2 Element 3",
      "Component 2 Element 4",
      "Component 2 Element 5",
      "Component 3 Type",
      "Component 3 Code",
      "Component 3 Item #",
      "Component 3 Desc",
      "Component 3 Qty",
      "Component 3 Cost",
      "Component 3 Element 1",
      "Component 3 Element 2",
      "Component 3 Element 3",
      "Component 3 Element 4",
      "Component 3 Element 5",
      "Component 4 Type",
      "Component 4 Code",
      "Component 4 Item #",
      "Component 4 Desc",
      "Component 4 Qty",
      "Component 4 Cost",
      "Component 4 Element 1",
      "Component 4 Element 2",
      "Component 4 Element 3",
      "Component 4 Element 4",
      "Component 4 Element 5",
      "Component 5 Type",
      "Component 5 Code",
      "Component 5 Item #",
      "Component 5 Desc",
      "Component 5 Qty",
      "Component 5 Cost",
      "Component 5 Element 1",
      "Component 5 Element 2",
      "Component 5 Element 3",
      "Component 5 Element 4",
      "Component 5 Element 5",
      "Component 6 Type",
      "Component 6 Code",
      "Component 6 Item #",
      "Component 6 Desc",
      "Component 6 Qty",
      "Component 6 Cost",
      "Component 6 Element 1",
      "Component 6 Element 2",
      "Component 6 Element 3",
      "Component 6 Element 4",
      "Component 6 Element 5",
      "Component 7 Type",
      "Component 7 Code",
      "Component 7 Item #",
      "Component 7 Desc",
      "Component 7 Qty",
      "Component 7 Cost",
      "Component 7 Element 1",
      "Component 7 Element 2",
      "Component 7 Element 3",
      "Component 7 Element 4",
      "Component 7 Element 5",
      "Component 8 Type",
      "Component 8 Code",
      "Component 8 Item #",
      "Component 8 Desc",
      "Component 8 Qty",
      "Component 8 Cost",
      "Component 8 Element 1",
      "Component 8 Element 2",
      "Component 8 Element 3",
      "Component 8 Element 4",
      "Component 8 Element 5",
      "Barcode",
    ]

    const dataRows = lineItems.map(item => [
      item?.productId || "—",
      item.description || "—",
      item.sku || "—",
      item.description || "—",
      "",
      doc?.vendor?.vendor_id,
      item.rate ?? 0,
      item.sku || "—",
      item.sellcost ?? 0,
      item?.vmCode ?? "--",
      "",
      "",
      "",
      dropdownMap?.vmCode5?.list?.find(i => i?.id == item?.vmCode5)?.[dropdownMap?.vmCode5?.label] || "",
      dropdownMap?.vmCode6?.list?.find(i => i?.id == item?.vmCode6)?.[dropdownMap?.vmCode6?.label] || "",
      dropdownMap?.vmCode7?.list?.find(i => i?.id == item?.vmCode7)?.[dropdownMap?.vmCode7?.label] || "",
      dropdownMap?.vmCode8?.list?.find(i => i?.id == item?.vmCode8)?.[dropdownMap?.vmCode8?.label] || "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      dropdownMap?.department?.list?.find(i => i?.id == item?.department)
        ?.departmentName || "",
      dropdownMap?.category?.list?.find(i => i?.id == item?.category)
        ?.categoryName || "",
      dropdownMap?.class?.list?.find(i => i?.id == item?.class)?.className ||
        "",
      dropdownMap?.stone?.list?.find(i => i?.id == item?.stone)?.stoneName ||
        "",
      dropdownMap?.programBuild?.list?.find(i => i?.id == item?.programBuild)
        ?.programBuildName || "",
      dropdownMap?.brand?.list?.find(i => i?.id == item?.brand)?.brandName ||
        "",
      doc?.store?.storeName,
      "",
      item?.qty ?? item?.quantity ?? null,
      doc?.invoiceNo !== "null" ? doc?.invoiceNo : doc?.memoNo,
      doc?.markup?.promotionCode,
      doc?.supplierCountry,
      null,
      "",
      "",
      "",
      "",
      "",
      "",
      doc?.memoNo !== "null" ? "T" : "F",
      "",
      "",
      item?.optionalPrice,
      "",
      dropdownMap?.componentType?.list?.find(i => i?.id == item?.componentType)?.[dropdownMap?.componentType?.label] || "",
      item?.componentCode,
      "",
      "",
      item?.qty,
      "",
      // Component 1 Elements
      "",
      "",
      "",
      "",
      "",
      // Component 2
      dropdownMap?.componentType2?.list?.find(i => i?.id == item?.componentType2)?.[dropdownMap?.componentType2?.label] || "",
      item?.componentCode2,
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      // Component 3
      "",
      dropdownMap?.componentType3?.list?.find(i => i?.id == item?.componentType3)?.[dropdownMap?.componentType3?.label] || "",
      item?.componentCode3,
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      // Component 4
      "",
      dropdownMap?.componentType4?.list?.find(i => i?.id == item?.componentType4)?.[dropdownMap?.componentType4?.label] || "",
      item?.componentCode4,
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      // Component 5
      "",
      dropdownMap?.componentType5?.list?.find(i => i?.id == item?.componentType5)?.[dropdownMap?.componentType5?.label] || "",
      item?.componentCode5,
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      // Component 6
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      // Component 7
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      // Component 8
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      item.productId,
    ])

    const numericColumnIndices = [7, 9, 33, 37, 38, 40, 41, 42, 47, 53, 54, 64, 65, 75, 76, 86, 87, 97, 98, 108, 109, 119, 120, 130, 131]
    const toNumericOrNull = v => {
      if (v == null || v === "" || v === "—" || v === "--") return null
      const n = Number(v)
      return Number.isFinite(n) ? n : null
    }
    const processedDataRows = dataRows.map(row => {
      const nr = [...row]
      numericColumnIndices.forEach(idx => {
        nr[idx - 1] = toNumericOrNull(nr[idx - 1])
      })
      return nr
    })

    // ── Build workbook ──────────────────────────────────────────────────────────
    const wb = new ExcelJS.Workbook()
    const ws = wb.addWorksheet("Invoice")

    const centerAlignment = {
      horizontal: "center",
      vertical: "middle",
      wrapText: true,
    }

    // Add rows
    const r1 = ws.addRow(headerRow1)
    const r2 = ws.addRow(headerRow2)

    const dataExcelRows = processedDataRows.map(row => ws.addRow(row))

    // Apply center alignment to every cell in every row
    ;[r1, r2, ...dataExcelRows].forEach(row => {
      row.eachCell({ includeEmpty: true }, cell => {
        cell.alignment = centerAlignment
        cell.font = { name: "Arial", size: 10 }
      })
    })

    numericColumnIndices.forEach(colIdx => {
      ws.getColumn(colIdx).numFmt = '0.00'
    })
    dataExcelRows.forEach(row => {
      numericColumnIndices.forEach(colIdx => {
        row.getCell(colIdx).numFmt = '0.00'
      })
    })

    // ── Auto column widths ──────────────────────────────────────────────────────
    // Measure max content length per column across all rows then set width
    const allRows = [headerRow1, headerRow2, ...processedDataRows]
    const colCount = Math.max(...allRows.map(r => r.length))

    for (let colIdx = 1; colIdx <= colCount; colIdx++) {
      let maxLen = 8 // minimum width
      allRows.forEach(row => {
        const val = row[colIdx - 1]
        const len = val != null ? String(val).length : 0
        if (len > maxLen) maxLen = len
      })
      ws.getColumn(colIdx).width = Math.min(maxLen + 4, 60) // cap at 60
    }

    // ── Export ──────────────────────────────────────────────────────────────────
    const invoiceNo =
      doc.invoiceNo !== "null"
        ? doc.invoiceNo
        : doc.memoNo !== "null"
        ? doc.memoNo
        : doc.id

    const fileName = `${doc?.vendor?.company}_${invoiceNo}_${
      doc.vendor?.vendorName?.trim() || "export"
    }.xlsx`

    // Browser download
    const buffer = await wb.xlsx.writeBuffer()
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = fileName
    a.click()
    URL.revokeObjectURL(url)
  }

  /* ─────────────────────────── RENDER ─────────────────────────── */
  return (
    <div className="page-content">
      {loading && <Loader />}
      <Breadcrumb title="Dashboard" breadcrumbItem="Inventory" />

      <Card className="shadow-sm border-0">
        <CardBody>
          {/* Filter bar */}
          <div className="d-flex justify-content-between mb-3 gap-2 flex-wrap">
            <div className="d-flex gap-2">
              <select
                className="form-select"
                style={{ width: 100 }}
                value={pageSize}
                onChange={e => {
                  setPageSize(Number(e.target.value))
                  setCurrentPage(1)
                }}
              >
                {[10, 20, 50].map(n => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <Input
                placeholder="Search..."
                value={searchString}
                onChange={e => {
                  setSearchString(e.target.value)
                  setCurrentPage(1)
                }}
                style={{ minWidth: 220 }}
              />
            </div>
            {checkAccess(1, "add") && (
              <Button
                className="btn-blue"
                onClick={() => {
                  navigate(`${Configs.appurl}import-invoice`)
                }}
              >
                <FaFileImport className="me-1" /> Import
              </Button>
            )}
          </div>

          {/* Table */}
          <Table striped responsive className="mb-3">
            <thead className="table-blue">
              <tr>
                <th>#</th>
                <th style={{ width: "30%" }}>File Name</th>
                <th>Invoice No / Memo No</th>
                <th>Vendor</th>
                <th>Amount</th>
                <th>Actions</th>
                <th>Export</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center text-muted py-4">
                    No Records Found
                  </td>
                </tr>
              ) : (
                paginatedData.map((doc, index) => (
                  <tr key={doc.id}>
                    <td>{(currentPage - 1) * pageSize + index + 1}</td>
                    <td>{doc.invoiceLink?.split("/")?.[1]}</td>
                    <td>
                      {doc.invoiceNo !== "null" ? doc.invoiceNo : doc?.memoNo}
                    </td>
                    <td>{`${doc?.vendor?.company} (${doc?.vendor?.vendor_id})`}</td>
                    <td>{doc.totalAmount ? `$ ${doc.totalAmount}` : "—"}</td>
                    <td className="d-flex gap-2">
                      <Button
                        className="btn-secondary"
                        onClick={() => {
                          setSelectedDocument(doc), setDocModal(true)
                          setIsEditing(false)
                        }}
                        title="View"
                      >
                        <FaEye />
                      </Button>
                      {checkAccess(1, "edit") && (
                        <Button
                          className="btn-success"
                          onClick={() => {
                            setSelectedDocument(doc), setDocModal(true)
                            setIsEditing(true)
                          }}
                          title="Edit"
                        >
                          <FaEdit />
                        </Button>
                      )}
                      <Button
                        className="btn-warning"
                        onClick={() =>
                          window.open(
                            `${Configs.imageUrl}${doc.invoiceLink}`,
                            "_blank",
                            "noopener,noreferrer"
                          )
                        }
                        title="PDF"
                      >
                        <FaFilePdf />
                      </Button>
                      {checkAccess(1, "delete") && (
                        <Button
                          color="danger"
                          onClick={() => handleDelete(doc.id)}
                          title="Delete"
                        >
                          <FaTrash />
                        </Button>
                      )}
                    </td>
                    <td>
                      <Button
                        className="btn-blue"
                        onClick={() => handleExport(doc)}
                        title="Export"
                      >
                        <FaFileExport /> Export
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          {/* Pagination */}
          <div className="d-flex justify-content-between align-items-center">
            <span className="text-muted small">
              Page {currentPage} of {totalPages || 1}
            </span>
            <div className="d-flex gap-1 align-items-center">
              <Button
                size="sm"
                className="btn-blue"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(p => p - 1)}
              >
                <FaChevronLeft />
              </Button>
              {getPageNumbers().map(n => (
                <Button
                  key={n}
                  size="sm"
                  color={currentPage === n ? "primary" : "light"}
                  onClick={() => setCurrentPage(n)}
                >
                  {n}
                </Button>
              ))}
              <Button
                size="sm"
                className="btn-blue"
                disabled={currentPage === totalPages || totalPages === 0}
                onClick={() => setCurrentPage(p => p + 1)}
              >
                <FaChevronRight />
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>
      {/* <UploadModal open={importModal} Close={() => setImportModal(false)} /> */}

      <Modal
        size="xl"
        centered
        isOpen={docModal}
        toggle={() => {
          setDocModal(false)
          setIsEditing(false)
          setSelectedDocument({})
        }}
      >
        <ModalHeader
          toggle={() => {
            setDocModal(false)
            setIsEditing(false)
            setSelectedDocument({})
          }}
        >
          <div className="d-flex align-items-center gap-3">
            <span>
              {isEditing ? "Edit" : "View"} Document —{" "}
              {selectedDocument?.vendor?.company}
            </span>
          </div>
        </ModalHeader>

        <ModalBody>
          <Row className="mb-3 g-2">
            <Col xl={6}>
              <Label className="fw-semibold mb-1">Vendor Name</Label>
              {isEditing ? (
                <Input
                  type="select"
                  value={selectedDocument?.vendorId}
                  onChange={e => updateField("vendorId", e.target.value)}
                  disabled
                >
                  <option value="">— Choose a vendor —</option>
                  {vendors.map(v => (
                    <option key={v.id} value={v.id}>
                      {`${v?.company}(${v?.vendor_id})`}
                    </option>
                  ))}
                </Input>
              ) : (
                <Input
                  disabled
                  value={`${selectedDocument?.vendor?.company}(${selectedDocument?.vendor?.vendor_id})`}
                />
              )}
            </Col>

            <Col xl={6}>
              <Label className="fw-semibold mb-1">Invoice Type</Label>
              <Input
                type="select"
                disabled={!isEditing}
                value={
                  selectedDocument?.invoiceNo !== "null" ? "invoice" : "memo"
                }
                onChange={e => updateField("type", e.target.value)}
              >
                <option value="">Select Type</option>
                <option value="invoice">Invoice</option>
                <option value="memo">Memo</option>
              </Input>
            </Col>

            {selectedDocument?.invoiceNo !== "null" && (
              <Col xl={6}>
                <Label className="fw-semibold mb-1">Invoice No</Label>
                <Input
                  disabled={!isEditing}
                  value={selectedDocument?.invoiceNo || ""}
                  onChange={e => updateField("invoiceNo", e.target.value)}
                />
              </Col>
            )}

            {selectedDocument?.memoNo != "null" && (
              <Col xl={6}>
                <Label className="fw-semibold mb-1">Memo No</Label>
                <Input
                  disabled={!isEditing}
                  value={selectedDocument?.memoNo || ""}
                  onChange={e => updateField("memoNo", e.target.value)}
                />
              </Col>
            )}

            <Col xl={6}>
              <Label className="fw-semibold mb-1">Invoice Date</Label>
              <Input
                type="date"
                disabled={!isEditing}
                value={toInputDate(selectedDocument?.invoiceDate) || ""}
                onChange={e => updateField("invoiceDate", e.target.value)}
              />
            </Col>

            <Col xl={6}>
              <Label className="fw-semibold mb-1">PO Number</Label>
              <Input
                disabled={!isEditing}
                value={selectedDocument?.poNumber || ""}
                onChange={e => updateField("poNumber", e.target.value)}
              />
            </Col>

            <Col xl={6}>
              <Label className="fw-semibold mb-1">Due Date</Label>
              <Input
                type="date"
                disabled={!isEditing}
                value={toInputDate(selectedDocument?.dueDate) || ""}
                onChange={e => updateField("dueDate", e.target.value)}
              />
            </Col>

            <Col xl={6}>
              <Label className="fw-semibold mb-1">Tracking No</Label>
              <Input
                disabled={!isEditing}
                value={selectedDocument?.trackingNo || ""}
                onChange={e => updateField("trackingNo", e.target.value)}
              />
            </Col>

            <Col xl={6}>
              <Label className="fw-semibold mb-1">Total Amount</Label>
              <Input
                disabled={!isEditing}
                value={selectedDocument?.totalAmount || ""}
                onChange={e => updateField("totalAmount", e.target.value)}
              />
            </Col>

            <Col xl={6}>
              <Label className="fw-semibold mb-1">Total Products</Label>
              <Input
                disabled
                value={
                  safeParseLineItems(selectedDocument?.lineItems)?.length || ""
                }
              />
            </Col>

            <Col xl={6}>
              <Label className="fw-semibold mb-1">Sender Country</Label>
              <Input
                disabled={!isEditing}
                value={selectedDocument?.supplierCountry || ""}
                onChange={e => updateField("supplierCountry", e.target.value)}
              />
            </Col>

            <Col xl={6}>
              <Label className="fw-semibold mb-1">Promotion Code</Label>
              <select
                className="form-select"
                disabled={!isEditing}
                value={selectedDocument?.promotionCode || ""}
                onChange={e => {
                  const selectedId = e.target.value

                  const selectedItem =
                    selectedDocument?.vendor?.vendorMarkups?.find(
                      item => item?.id == selectedId
                    )

                  updateField("promotionCode", selectedId)
                  updateField("msrpMarkup", selectedItem?.markup)
                }}
              >
                <option>Select Promotion Code</option>
                {selectedDocument?.vendor?.vendorMarkups?.map(item => (
                  <option key={item?.id} value={item?.id}>
                    {item?.promotionCode}
                  </option>
                ))}
              </select>
            </Col>

            <Col xl={6}>
              <Label className="fw-semibold mb-1">MSRP Markup</Label>
              <Input
                disabled={!isEditing}
                value={selectedDocument?.msrpMarkup ?? ""}
                onChange={e => updateField("msrpMarkup", e.target.value)}
              />
            </Col>

            <Col xl={6}>
              <Label className="fw-semibold mb-1">Store</Label>
              <select
                className="form-select"
                disabled={!isEditing}
                value={selectedDocument?.storeId || ""}
                onChange={e => updateField("storeId", e.target.value)}
              >
                <option value={""}>Select Store</option>
                {dropdownMap?.store?.list?.length > 0 &&
                  dropdownMap.store.list.map(o => (
                    <option value={o?.id} key={o?.id}>
                      {o?.storeName}
                    </option>
                  ))}
              </select>
            </Col>

            <Col xl={6}>
              <Label className="fw-semibold mb-1">Store Mark Up (%)</Label>
              <Input
                disabled={!isEditing}
                value={selectedDocument?.invMarkup ?? 10}
                onChange={e => updateField("invMarkup", e.target.value)}
              />
            </Col>

            <Col xl={12}>
              <Label className="fw-semibold mb-1">Bill To</Label>
              <Input
                type="textarea"
                rows={2}
                disabled={!isEditing}
                value={selectedDocument?.billTo || ""}
                onChange={e => updateField("billTo", e.target.value)}
              />
            </Col>
          </Row>

          <h6 className="fw-semibold mb-2" style={{ color: "#1a237e" }}>
            Line Items
          </h6>
          <div style={{ maxHeight: 280, fontSize: 12, overflow: "auto" }}>
            <Table bordered style={{ minWidth: 2800, borderCollapse: "separate", borderSpacing: 0 }}>
              <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 2 }}>
                <tr>
                  {[
                    { label: "#", width: 40 },
                    { label: "Style", width: 120 },
                    { label: "Description", width: 280 },
                    { label: "QTY", width: 70 },
                    { label: "Rate", width: 90 },
                    { label: "Amount", width: 90 },
                    { label: "Optional Price 1", width: 130 },
                    { label: "MSRP Markup", width: 130 },
                    { label: "MSRP Selling Value", width: 170 },
                    { label: "VM Code 1", width: 150 },
                    { label: "VM Code 5", width: 150 },
                    { label: "VM Code 6", width: 150 },
                    { label: "VM Code 7", width: 150 },
                    { label: "VM Code 8", width: 150 },
                    { label: "Vendor Model", width: 130 },
                    { label: "Department", width: 120 },
                    { label: "Category", width: 120 },
                    { label: "Class Name", width: 120 },
                    { label: "Program Builds", width: 130 },
                    { label: "Stone", width: 100 },
                    { label: "Brand", width: 100 },
                    { label: "Component Type 1", width: 150 },
                    { label: "Component Code 1", width: 140 },
                    { label: "Component Type 2", width: 150 },
                    { label: "Component Code 2", width: 140 },
                    { label: "Component Type 3", width: 150 },
                    { label: "Component Code 3", width: 140 },
                    { label: "Component Type 4", width: 150 },
                    { label: "Component Code 4", width: 140 },
                    { label: "Component Type 5", width: 150 },
                    { label: "Component Code 5", width: 140 },
                  ].map(({ label, width }, colIdx) => (
                    <th
                      key={label}
                      style={{
                        minWidth: width,
                        whiteSpace: "nowrap",
                        ...(colIdx === 0 && { position: "sticky", left: 0, zIndex: 1, background: "#f8f9fa" }),
                        ...(colIdx === 1 && { position: "sticky", left: 40, zIndex: 1, background: "#f8f9fa" }),
                      }}
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(selectedDocument?.lineItems
                  ? safeParseLineItems(selectedDocument?.lineItems)
                  : []
                ).map((item, index) => (
                  <tr key={index}>
                    <td className="text-center align-middle" style={{ position: "sticky", left: 0, zIndex: 1, background: "#fff", minWidth: 40 }}>{index + 1}</td>
                    {[
                      { field: "sku", width: 120 },
                      { field: "description", width: 280 },
                      { field: "qty", width: 70 },
                      { field: "rate", width: 90 },
                      { field: "amount", width: 90 },
                      { field: "optionalPrice", width: 130 },
                      { field: "discount", width: 130 },
                      { field: "sellcost", width: 170 },
                      { field: "vmCode", width: 150 },
                      { field: "vmCode5", width: 150 },
                      { field: "vmCode6", width: 150 },
                      { field: "vmCode7", width: 150 },
                      { field: "vmCode8", width: 150 },
                      { field: "vendorModal", width: 130 },
                      { field: "department", width: 150 },
                      { field: "category", width: 150 },
                      { field: "class", width: 150 },
                      { field: "programBuild", width: 150 },
                      { field: "stone", width: 200 },
                      { field: "brand", width: 150 },
                      { field: "componentType", width: 150 },
                      { field: "componentCode", width: 140 },
                      { field: "componentType2", width: 150 },
                      { field: "componentCode2", width: 140 },
                      { field: "componentType3", width: 150 },
                      { field: "componentCode3", width: 140 },
                      { field: "componentType4", width: 150 },
                      { field: "componentCode4", width: 140 },
                      { field: "componentType5", width: 150 },
                      { field: "componentCode5", width: 140 },
                    ].map(({ field, width }, colIdx) => (
                      <td
                        key={field}
                        style={{
                          minWidth: width,
                          padding: "2px 4px",
                          ...(colIdx === 0 && { position: "sticky", left: 40, zIndex: 1, background: "#fff" }),
                        }}
                      >
                        {[
                          "department",
                          "category",
                          "class",
                          "programBuild",
                          "stone",
                          "brand",
                          "vmCode5",
                          "vmCode6",
                          "vmCode7",
                          "vmCode8",
                          "componentType",
                          "componentType2",
                          "componentType3",
                          "componentType4",
                          "componentType5",
                        ].includes(field) ? (
                          <Select
                            isDisabled={!isEditing}
                            value={
                              dropdownMap[field]?.list
                                ?.map(o => ({
                                  value: o.id,
                                  label: o[dropdownMap[field].label],
                                }))
                                ?.find(o => o.value === item[field]) || null
                            }
                            onChange={s =>
                              updateLineItem(index, field, s?.value)
                            }
                            options={dropdownMap[field]?.list?.map(o => ({
                              value: o.id,
                              label: o[dropdownMap[field].label],
                            }))}
                            placeholder="Select..."
                            isClearable
                            // ✅ IMPORTANT FIX
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={customStyles}
                          />
                        ) : (
                          <Input
                            bsSize="sm"
                            disabled={!isEditing}
                            value={item[field] ?? ""}
                            onChange={e =>
                              updateLineItem(index, field, e.target.value)
                            }
                            style={{ width: "100%", minWidth: width - 8 }}
                          />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </ModalBody>

        {/* Footer only shows when editing */}
        {isEditing && (
          <ModalFooter>
            <Button className="btn-blue" onClick={() => handleEdit()}>
              <MdSave className="me-1" /> Save
            </Button>
            <Button
              color="danger"
              onClick={() => {
                setSelectedDocument({})
                setDocModal(false)
                setIsEditing(false)
              }}
            >
              <MdCancel className="me-1" /> Cancel
            </Button>
          </ModalFooter>
        )}
      </Modal>
    </div>
  )
}

export default DocumentIndex
